import { useEffect } from 'react';

import { extractDayInTimezone, formatDate, subtractTime, toDate } from '@/utils/date';

/** The backend's default overview window: 28 days, the last one included. Matches *Last 4 weeks*. */
const DEFAULT_DATE_RANGE_DAYS = 28;

export interface HydrateOverviewDisplayDatesParams {
    /** `lastDay` from the overview response, or undefined while it loads. */
    lastDay: string | undefined;
    /** The overview request's loading flag; false once it has settled, even on error. */
    isLoading: boolean;
    /** Query dates, from the URL. Present means the user picked a range. */
    queryDateFrom: string | undefined;
    queryDateTo: string | undefined;
    /** The cage location's timezone; the backend's window ends on today at the site. */
    timezone: string | undefined;
    setDisplayDates: (from: string | undefined, to: string | undefined) => void;
}

// The browser's date can already be tomorrow or still yesterday at the site.
function siteToday(timezone: string | undefined): string {
    return timezone
        ? extractDayInTimezone(new Date().toISOString(), timezone)
        : formatDate(new Date(), 'internal');
}

/**
 * Fills the date picker with a default range once the overview has loaded.
 *
 * Pair this with `useCageOverviewDateRange`, which owns the `date-from` /
 * `date-to` URL params. When the URL carries no range, the picker would otherwise
 * sit empty: this shows the four weeks ending at the backend's last measurement day, or
 * ending today once the overview settles without one, while deliberately leaving the
 * *query* dates unset so the request still goes out without date params and the
 * backend's own defaults apply.
 *
 * It has to be an effect rather than a derived value because `lastDay` comes from
 * the very response the query dates feed into — reading it during render would be
 * circular. Call it after the overview query in the component body.
 */
export function useHydrateOverviewDisplayDates(params: HydrateOverviewDisplayDatesParams): void {
    const { lastDay, isLoading, queryDateFrom, queryDateTo, timezone, setDisplayDates } = params;

    useEffect(() => {
        // The user's own range wins; never overwrite it.
        if (queryDateFrom != null && queryDateTo != null) {
            return;
        }

        // A cage that never measured, or a failed request, settles without a lastDay.
        // Empty dates kept the picker waiting for good, so it gets the four weeks ending
        // today, as the backend's window policy gives a cage with no data day.
        const endDay = lastDay ?? (isLoading ? undefined : siteToday(timezone));
        if (!endDay) {
            return;
        }

        const startDate = formatDate(
            subtractTime(toDate(endDay), DEFAULT_DATE_RANGE_DAYS - 1, 'day'),
            'internal'
        );

        if (startDate) {
            setDisplayDates(startDate, endDay);
        }
    }, [lastDay, isLoading, queryDateFrom, queryDateTo, timezone, setDisplayDates]);
}
